import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Spinner } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, KeyRound, AlertCircle, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import BeartrackLogo from "../../assets/beartrack/beartrack_logo.png";
import { recoveryCodeLogin, recoveryPassword } from "../../api/auth";
import { showToast } from "../../utils/showToast";
import { persistSessionAndRedirect } from "../../utils/sessionRedirect";
import LoginAside from "./LoginAside";

const RESEND_COOLDOWN_SECONDS = 60;

export default function EnterRecoveryCode() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // El correo se recibe por state del router: Clarity graba las URLs y no debe verlo.
  // `?email=` se sigue aceptando solo para enlaces antiguos o ya enviados.
  const legacyEmail = searchParams.get("email") || "";
  const stateEmail = typeof location.state?.email === "string" ? location.state.email : "";
  const prefilledEmail = stateEmail || legacyEmail;
  const { control, handleSubmit, getValues, watch, setError, clearErrors, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimer = useRef(null);

  // Borra el correo de la URL pero lo conserva en el state (sobrevive al reload).
  useEffect(() => {
    if (legacyEmail) {
      navigate(location.pathname, { replace: true, state: { email: legacyEmail } });
    }
  }, [legacyEmail, location.pathname, navigate]);

  useEffect(() => () => clearInterval(cooldownTimer.current), []);

  // Recién llegado desde "Enviar código": ese código acaba de salir, y reenviar al tiro
  // lo invalidaría antes de que alcance a llegar. Se lee una sola vez al montar.
  const justSentRef = useRef(location.state?.justSent === true);

  const startCooldown = useCallback(() => {
    clearInterval(cooldownTimer.current);
    setCooldown(RESEND_COOLDOWN_SECONDS);
    cooldownTimer.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          clearInterval(cooldownTimer.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    if (justSentRef.current) startCooldown();
  }, [startCooldown]);

  const resendCode = async () => {
    const email = (getValues("email") || "").trim();
    if (!email) {
      setError("email", { type: "required", message: t("Ingrese un correo válido.") });
      return;
    }
    clearErrors("email");
    setResending(true);
    const result = await recoveryPassword({ email, method: "code" });
    setResending(false);

    if (!result.success) {
      showToast(
        result.error?.code === "RATE_LIMITED"
          ? t("Demasiados intentos. Espera unos minutos antes de volver a solicitarlo.")
          : t("No pudimos enviar tu solicitud. Revisa tu conexión e inténtalo de nuevo."),
        false
      );
      // Con 429 el backend ya pidió esperar: el cooldown evita seguir golpeándolo.
      if (result.error?.code === "RATE_LIMITED") startCooldown();
      return;
    }
    // Respuesta neutra: el backend contesta igual exista o no la cuenta (anti-enumeración).
    showToast(
      t("Si existe una cuenta vinculada a ese correo, te enviamos un nuevo código. Usa el más reciente y revisa también la carpeta de spam."),
      true
    );
    startCooldown();
  };

  const onSubmit = async ({ email, otp }) => {
    setLoading(true);
    try {
      const res = await recoveryCodeLogin({ email: email.trim(), otp: otp.trim() });

      if (!res.success) {
        if (res.error?.code === "PAYMENT_BLOCKED") {
          showToast(res.error.message || t("Tu cuenta está suspendida por falta de pago."), false);
        } else {
          // The backend funnels every OTP failure into one 400 (anti-enumeration), so
          // the nuance comes from the code recoveryCodeLogin classified.
          const codeMessages = {
            RATE_LIMITED: t("Demasiados intentos. Espera unos minutos antes de volver a intentar."),
            ACCOUNT_BLOCKED: t("Tu cuenta está bloqueada. Contacta a tu administrador. El código ya se usó."),
            NETWORK: t("No pudimos conectar con el servidor. Intenta nuevamente."),
          };
          showToast(codeMessages[res.error?.code] || t("Código inválido o expirado."), false);
        }
        setLoading(false);
        return;
      }

      const result = persistSessionAndRedirect(res.data);
      if (!result.ok) {
        // Token válido pero el rol no opera en el portal web (p. ej. repartidor).
        showToast(t("Esta cuenta no puede ingresar al portal web."), false);
        setLoading(false);
      }
      // Si result.ok, persistSessionAndRedirect ya redirigió (window.location.replace).
    } catch (error) {
      showToast(t("Ocurrió un error, intente nuevamente."), false);
      setLoading(false);
    }
  };

  return (
    <div className="login-split">
      <LoginAside />
      <main className="login-main">
        <div className="login-main__inner">
          <div className="login-logo-container login-logo-container--mobile">
            <img src={BeartrackLogo} alt="BearTrack" className="login-logo" />
          </div>

          <div className="login-header">
            <h1 className="login-title">{t("Ingresa tu código")}</h1>
            <p className="login-subtitle">{t("Escribe el código de un solo uso que enviamos a tu correo.")}</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="login-form" noValidate>
            <div className="login-form-group">
              <div className="login-field">
                <label htmlFor="rc-email" className="login-label">{t("Correo electrónico")}</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><Mail size={18} strokeWidth={2} /></span>
                  <Controller
                    name="email"
                    control={control}
                    defaultValue={prefilledEmail}
                    rules={{ required: t("Ingrese un correo válido.") }}
                    render={({ field }) => (
                      <input
                        {...field}
                        id="rc-email"
                        type="email"
                        autoComplete="email"
                        className="login-input"
                        placeholder={t("tucorreo@gmail.com")}
                      />
                    )}
                  />
                </div>
                {errors.email && (
                  <div className="login-error" role="alert">
                    <AlertCircle size={14} />
                    {errors.email.message}
                  </div>
                )}
              </div>
            </div>

            <div className="login-form-group">
              <div className="login-field">
                <label htmlFor="rc-otp" className="login-label">{t("Código de un solo uso")}</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><KeyRound size={18} strokeWidth={2} /></span>
                  <Controller
                    name="otp"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: t("Ingrese el código."),
                      pattern: { value: /^[0-9]{4,10}$/, message: t("El código debe ser numérico.") },
                    }}
                    render={({ field }) => (
                      <input
                        {...field}
                        id="rc-otp"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={10}
                        className="login-input"
                        placeholder="••••••"
                      />
                    )}
                  />
                </div>
                {errors.otp && (
                  <div className="login-error" role="alert">
                    <AlertCircle size={14} />
                    {errors.otp.message}
                  </div>
                )}
              </div>
            </div>

            <div className="login-actions">
              <Button
                type="submit"
                className={`login-button ${loading ? "loading" : ""}`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t("Ingresando…")}
                  </>
                ) : (
                  t("Ingresar")
                )}
              </Button>

              <div className="login-forgot">
                <Button
                  type="button"
                  color="link"
                  className="login-forgot-link p-0 border-0 align-baseline"
                  onClick={resendCode}
                  disabled={resending || cooldown > 0 || loading}
                >
                  {cooldown > 0
                    ? t("Reenviar código en {{seconds}} s", { seconds: cooldown })
                    : t("Reenviar código")}
                </Button>
              </div>

              <div className="login-forgot">
                <Link
                  to="/login/recovery"
                  state={{ email: watch("email") || "", method: "code" }}
                  className="login-back-link"
                >
                  <ArrowLeft size={14} /> {t("Usar otro correo o método")}
                </Link>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
